import { FC, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, Space, Typography } from 'antd';
import { capitalCase } from 'change-case';
import {
    TbActivity,
    TbArrowUpRight,
    TbClock,
    TbLink,
    TbLockAccess,
    TbMessageCircle,
    TbRestore,
    TbSquareRoundedPlus2,
    TbTrash,
    TbUsers,
} from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useNestedResourceAccess } from '@/hooks/useNestedResourceAccess';
import { useResourceCollaborators } from '@/hooks/useResourceCollaborators';
import { useResourceActionAccess } from '@/hooks/useResourceActionAccess';
import type { WorkspaceResource } from '@/hooks/useWorkspaceResourceAccess';
import { useWorkspaceResourceAccess } from '@/hooks/useWorkspaceResourceAccess';
import { memoService, reportService } from '@/services/database';
import { emitResourceActivityOpenEvent } from '@/shared/components/activity';
import { isCommentableWorkspaceResource } from '@/shared/components/activity/resourceCommentAccess';
import {
    COMPACT_HOVER_CARD_METADATA_ICON_SIZE,
    CompactHoverCardIdentity,
    CompactHoverCardMetadataRow,
    CompactHoverCardSection,
    CompactHoverCardSurface,
} from '@/shared/components/cards';
import { CompactActiveAvatarGroup } from '@/shared/components/groups';
import EmptyView from '@/shared/components/layouts/views/EmptySection';
import { BaseSkeleton, ButtonSkeleton } from '@/shared/components/skeletons';
import { SmallTag } from '@/shared/components/tags';
import { WorkspaceResourceAvatar } from '@/shared/components/workspace-resources';
import { formatDate } from '@/utilities';

import { WORKSPACE_RESOURCE_PREVIEW_REGISTRY } from './registry';
import type { WorkspaceResourcePreviewContentProps } from './types';

const iconStyle = { verticalAlign: '-0.125rem' } as const;

interface ResourceDetailSnapshot {
    collaboratorCount?: number;
    createdAt?: Date | string;
    deletedAt?: Date | string;
    description?: string;
    label?: string;
    updatedAt?: Date | string;
    website?: string;
}

const nestedResourceDetailLoaders = {
    reports: (id: string) => reportService.getById(id),
};

const recordValue = (value: unknown): Record<string, unknown> | undefined =>
    typeof value === 'object' && value !== null && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : undefined;

const stringValue = (record: Record<string, unknown>, keys: string[]) => {
    for (const key of keys) {
        const value = record[key];

        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }

    return undefined;
};

const dateValue = (record: Record<string, unknown>, key: string) => {
    const value = record[key];

    return value instanceof Date || typeof value === 'string' ? value : undefined;
};

const toResourceDetailSnapshot = (value: unknown): ResourceDetailSnapshot => {
    const record = recordValue(value);

    if (!record) {
        return {};
    }

    return {
        collaboratorCount: typeof record.collaboratorCount === 'number' ? record.collaboratorCount : undefined,
        createdAt: dateValue(record, 'createdAt'),
        deletedAt: dateValue(record, 'deletedAt'),
        description: stringValue(record, ['description', 'summary']),
        label: stringValue(record, ['editedName', 'name', 'title']),
        updatedAt: dateValue(record, 'updatedAt'),
        website: stringValue(record, ['website']),
    };
};

const WorkspaceResourceHoverCardContent: FC<WorkspaceResourcePreviewContentProps> = ({
    accessMode = 'authenticated',
    actionsVisible = true,
    data,
    onRequestClose,
    open,
    publicMemoId,
}) => {
    const { token } = useAntDesignTheme();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [restored, setRestored] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const workspaceResourceType = isCommentableWorkspaceResource(data.resourceType) ? data.resourceType : undefined;
    const isWorkspaceResource = Boolean(workspaceResourceType);
    const accessResource: WorkspaceResource = workspaceResourceType ?? 'folders';
    // Lifecycles are dispatched to their registered rich renderer before this generic component is rendered.
    const collaboratorResourceType = accessResource as Exclude<WorkspaceResource, 'lifecycles'>;
    const resourceId = workspaceResourceType ? data.id : undefined;
    const access = useWorkspaceResourceAccess(accessResource, resourceId);
    const reportAccess = useNestedResourceAccess('reports', data.resourceType === 'reports' ? data.id : undefined);
    const teamReadAccess = useResourceActionAccess(
        data.resourceType === 'teams' ? 'teams' : undefined,
        'read',
        data.resourceType === 'teams' ? data.id : undefined,
    );
    const canRead =
        accessMode === 'public'
            ? Boolean(publicMemoId)
            : workspaceResourceType
              ? access.canRead
              : data.resourceType === 'reports'
                ? reportAccess.canRead
                : data.resourceType === 'teams'
                  ? teamReadAccess.allowed
                  : false;
    const initiallyInTrash = Boolean(data.deletedAt);
    const loader = workspaceResourceType
        ? WORKSPACE_RESOURCE_PREVIEW_REGISTRY[workspaceResourceType].loadDetail
        : data.resourceType === 'reports'
          ? nestedResourceDetailLoaders.reports
          : undefined;
    const detailQuery = useQuery({
        enabled:
            open &&
            canRead &&
            (accessMode === 'public' ? Boolean(publicMemoId) : Boolean(loader)) &&
            !initiallyInTrash,
        queryFn: async () =>
            toResourceDetailSnapshot(
                accessMode === 'public' && publicMemoId
                    ? await memoService.getPublicMention(publicMemoId, data.resourceType, data.id)
                    : await loader?.(data.id),
            ),
        queryKey: ['workspace-resource-hover-card', accessMode, publicMemoId, data.resourceType, data.id],
        retry: false,
        staleTime: 60_000,
    });
    const detail = detailQuery.data;
    const inTrash = !restored && Boolean(data.deletedAt ?? detail?.deletedAt);
    const { collaborators, loading: collaboratorsLoading } = useResourceCollaborators(
        collaboratorResourceType,
        accessMode === 'authenticated' && open && canRead && isWorkspaceResource && !inTrash ? data.id : undefined,
    );
    const collaboratorsAreLoading = accessMode === 'authenticated' && collaboratorsLoading;
    const label = detail?.label ?? data.label;
    const createdAt = detail?.createdAt ?? (accessMode === 'authenticated' ? data.createdAt : undefined);
    const updatedAt = detail?.updatedAt ?? (accessMode === 'authenticated' ? data.updatedAt : undefined);
    const summary = accessMode === 'public' ? detail?.description : (data.summary ?? detail?.description);
    const website = detail?.website ?? (accessMode === 'authenticated' ? data.website : undefined);
    const collaboratorCount = detail?.collaboratorCount ?? collaborators?.length ?? 0;
    const resourceLabel = useMemo(() => capitalCase(data.resourceType.replace(/s$/, '')), [data.resourceType]);

    useEffect(() => {
        if (detailQuery.error && accessMode === 'authenticated') {
            captureException(detailQuery.error);
        }
    }, [accessMode, detailQuery.error]);

    const openResourceActivity = (tab?: 'activity' | 'comments') => {
        onRequestClose();

        if (data.href) {
            navigate(data.href);
        }

        if (tab) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    emitResourceActivityOpenEvent({
                        resourceId: data.id,
                        resourceType: data.resourceType,
                        tab,
                    });
                });
            });
        }
    };

    const restoreResource = async () => {
        const restore = workspaceResourceType
            ? WORKSPACE_RESOURCE_PREVIEW_REGISTRY[workspaceResourceType].restore
            : undefined;

        if (!workspaceResourceType || !restore || restoring) {
            return;
        }

        setRestored(true);
        setRestoring(true);

        try {
            await restore(data.id);
            await queryClient.invalidateQueries({
                queryKey: ['workspace-resource-hover-card', accessMode, publicMemoId, data.resourceType, data.id],
            });
            toast.success(`${resourceLabel} Restored`);
        } catch (error) {
            setRestored(false);
            captureException(error);
            toast.error(`${resourceLabel} Restore Failed`, { description: 'Please try again.' });
        } finally {
            setRestoring(false);
        }
    };

    const canComment = isWorkspaceResource && access.canComment && !inTrash;
    const canViewActivity = isWorkspaceResource && access.canRead && !inTrash;
    const canRestore =
        isWorkspaceResource &&
        access.canManage &&
        inTrash &&
        Boolean(workspaceResourceType && WORKSPACE_RESOURCE_PREVIEW_REGISTRY[workspaceResourceType].restore);
    const showActions =
        actionsVisible &&
        (canComment || canViewActivity || canRestore || Boolean(canRead && data.href && !inTrash));

    if (!canRead || detailQuery.isError) {
        return (
            <CompactHoverCardSurface style={{ marginTop: '0.5rem' }} width='23rem'>
                <EmptyView
                    description={
                        accessMode === 'public'
                            ? 'Metadata for this resource is not available in the shared memo.'
                            : 'You do not have permission to view this workspace resource.'
                    }
                    height='12rem'
                    icon={<TbLockAccess size='75%' />}
                    label={accessMode === 'public' ? 'Resource Metadata Unavailable' : 'Resource Access Required'}
                />
            </CompactHoverCardSurface>
        );
    }

    return (
        <CompactHoverCardSurface style={{ marginTop: '0.5rem' }} width='23rem'>
            <div style={{ padding: '0.75rem' }}>
                <CompactHoverCardIdentity
                    avatar={
                        <WorkspaceResourceAvatar
                            listType={data.listType}
                            resource={data.resourceType}
                            size={40}
                            website={website}
                        />
                    }
                    subtitle={
                        <>
                            {resourceLabel}
                            {data.eventLabel ? ` · ${data.eventLabel}` : ''}
                        </>
                    }
                    title={label}
                    trailing={
                        inTrash ? (
                            <SmallTag
                                bordered
                                icon={<TbTrash size='0.75rem' style={{ marginRight: '0.25rem' }} />}
                                message='In Trash'
                                styles={{
                                    backgroundColor: token.volcano1,
                                    borderColor: token.volcano3,
                                    color: token.volcano6,
                                    marginLeft: 0,
                                }}
                            />
                        ) : data.status ? (
                            <Avatar
                                size={8}
                                style={{
                                    backgroundColor:
                                        data.status === 'unread' ? token.colorPrimary : token.colorTextQuaternary,
                                }}
                            />
                        ) : undefined
                    }
                />
            </div>

            <CompactHoverCardSection>
                <Space direction='vertical' size={8} style={{ width: '100%' }}>
                    {(isWorkspaceResource || detail?.collaboratorCount !== undefined) && !inTrash && (
                        <CompactHoverCardMetadataRow
                            icon={<TbUsers size={COMPACT_HOVER_CARD_METADATA_ICON_SIZE} />}
                            trailing={
                                accessMode === 'authenticated' ? (
                                    <CompactActiveAvatarGroup
                                        collaborators={collaborators}
                                        loading={collaboratorsAreLoading}
                                    />
                                ) : undefined
                            }
                        >
                            {collaboratorsAreLoading ? (
                                <ButtonSkeleton
                                    size='small'
                                    style={{
                                        display: 'block',
                                        height: '0.75rem',
                                        margin: 0,
                                        width: '6rem',
                                    }}
                                />
                            ) : (
                                `${collaboratorCount} ${collaboratorCount === 1 ? 'Collaborator' : 'Collaborators'}`
                            )}
                        </CompactHoverCardMetadataRow>
                    )}
                    {detailQuery.isLoading && !createdAt && !updatedAt && (
                        <>
                            <CompactHoverCardMetadataRow
                                icon={<TbSquareRoundedPlus2 size={COMPACT_HOVER_CARD_METADATA_ICON_SIZE} />}
                            >
                                <ButtonSkeleton
                                    size='small'
                                    style={{
                                        display: 'block',
                                        height: '0.75rem',
                                        margin: 0,
                                        width: '4.75rem',
                                    }}
                                />
                            </CompactHoverCardMetadataRow>
                            <CompactHoverCardMetadataRow
                                icon={<TbClock size={COMPACT_HOVER_CARD_METADATA_ICON_SIZE} />}
                            >
                                <ButtonSkeleton
                                    size='small'
                                    style={{
                                        display: 'block',
                                        height: '0.75rem',
                                        margin: 0,
                                        width: '4.75rem',
                                    }}
                                />
                            </CompactHoverCardMetadataRow>
                        </>
                    )}
                    {createdAt && (
                        <CompactHoverCardMetadataRow
                            icon={<TbSquareRoundedPlus2 size={COMPACT_HOVER_CARD_METADATA_ICON_SIZE} />}
                        >
                            Created {formatDate(new Date(createdAt), 'mini')}
                        </CompactHoverCardMetadataRow>
                    )}
                    {updatedAt && (
                        <CompactHoverCardMetadataRow icon={<TbClock size={COMPACT_HOVER_CARD_METADATA_ICON_SIZE} />}>
                            Updated {formatDate(new Date(updatedAt), 'mini')}
                        </CompactHoverCardMetadataRow>
                    )}
                    {data.parentName && (
                        <CompactHoverCardMetadataRow icon={<TbLink size={COMPACT_HOVER_CARD_METADATA_ICON_SIZE} />}>
                            Located in {data.parentName}
                        </CompactHoverCardMetadataRow>
                    )}
                    {data.actorName && (
                        <CompactHoverCardMetadataRow icon={<TbActivity size={COMPACT_HOVER_CARD_METADATA_ICON_SIZE} />}>
                            Last changed by {data.actorName}
                        </CompactHoverCardMetadataRow>
                    )}
                    {detailQuery.isLoading && !summary ? (
                        <BaseSkeleton
                            paragraph={{ rows: 3, width: ['100%', '100%', '70%'] }}
                            style={{ marginTop: '0.125rem' }}
                            title={false}
                        />
                    ) : summary ? (
                        <Typography.Paragraph
                            ellipsis={data.resourceType === 'investments' ? { rows: 3 } : undefined}
                            style={{
                                color: token.colorTextSecondary,
                                marginBottom: 0,
                            }}
                            title={data.resourceType === 'investments' ? summary : undefined}
                        >
                            {summary}
                        </Typography.Paragraph>
                    ) : null}
                </Space>
            </CompactHoverCardSection>

            {showActions && (
                <CompactHoverCardSection style={{ padding: '0.5rem 0.75rem' }}>
                    <Space.Compact block>
                        {canComment && (
                            <Button
                                icon={<TbMessageCircle style={iconStyle} />}
                                onClick={() => openResourceActivity('comments')}
                                size='small'
                                style={{ flex: 1 }}
                            >
                                Comment
                            </Button>
                        )}
                        {canViewActivity && (
                            <Button
                                icon={<TbActivity style={iconStyle} />}
                                onClick={() => openResourceActivity('activity')}
                                size='small'
                                style={{ flex: 1 }}
                            >
                                Activity
                            </Button>
                        )}
                        {canRestore && (
                            <Button
                                icon={<TbRestore style={iconStyle} />}
                                loading={restoring}
                                onClick={restoreResource}
                                size='small'
                                style={{ flex: 1 }}
                            >
                                Restore
                            </Button>
                        )}
                        {data.href && !inTrash && (
                            <Button
                                aria-label={`Open ${resourceLabel}`}
                                icon={<TbArrowUpRight style={iconStyle} />}
                                onClick={() => openResourceActivity()}
                                size='small'
                            />
                        )}
                    </Space.Compact>
                </CompactHoverCardSection>
            )}
        </CompactHoverCardSurface>
    );
};

export default WorkspaceResourceHoverCardContent;
